import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { authService } from '../../services/auth-service.js';
import '../../components/voice-modal.js';

const API_BASE = '/voice/php/api';

class SettingsSetupTab extends LitElement {
  static properties = {
    _loading: { state: true },
    _showConfirm: { state: true },
    _result: { state: true },
  };

  static styles = [designSystem, componentStyles, settingsStyles, css`
    .setup-card {
      text-align: center;
      padding: var(--space-8) var(--space-6);
    }
    .setup-icon {
      font-size: 3rem;
      color: var(--color-primary);
      margin-bottom: var(--space-4);
    }
    .setup-title {
      font-size: var(--text-xl);
      font-weight: 600;
      color: var(--color-text);
      margin-bottom: var(--space-2);
    }
    .setup-desc {
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
      max-width: 500px;
      margin: 0 auto var(--space-6);
      line-height: 1.6;
    }
    .btn-restore {
      background: #dc2626;
      color: #fff;
      border: none;
      padding: 10px 24px;
      border-radius: 8px;
      font-size: var(--text-sm);
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }
    .btn-restore:hover { background: #b91c1c; }
    .btn-restore:disabled { opacity: 0.5; cursor: not-allowed; }
    .result-box {
      margin-top: var(--space-4);
      padding: var(--space-3) var(--space-4);
      border-radius: 8px;
      font-size: var(--text-sm);
    }
    .result-success {
      background: #ecfdf5;
      color: #065f46;
      border: 1px solid #a7f3d0;
    }
    .result-error {
      background: #fef2f2;
      color: #991b1b;
      border: 1px solid #fecaca;
    }
    .modal-warning {
      font-size: var(--text-sm);
      line-height: 1.6;
      color: var(--color-text-secondary);
    }
    .modal-warning strong { color: var(--color-text); }
    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--space-2);
    }
    .btn-cancel {
      background: var(--color-bg-subtle);
      color: var(--color-text-secondary);
      border: 1px solid var(--color-border);
      padding: 8px 16px;
      border-radius: 6px;
      font-size: var(--text-sm);
      cursor: pointer;
    }
    .btn-confirm-danger {
      background: #dc2626;
      color: #fff;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: var(--text-sm);
      font-weight: 600;
      cursor: pointer;
    }
    .btn-confirm-danger:hover { background: #b91c1c; }
    .btn-confirm-danger:disabled { opacity: 0.5; cursor: not-allowed; }
  `];

  constructor() {
    super();
    this._loading = false;
    this._showConfirm = false;
    this._result = null;
  }

  async _restore() {
    this._showConfirm = false;
    this._loading = true;
    this._result = null;

    try {
      const res = await authService.apiFetch(`${API_BASE}/setup-defaults.php`, { method: 'POST' });
      const data = await res.json();

      if (data.success) {
        this._result = { type: 'success', message: data.message || `Restored ${data.count} settings.`, errors: null };
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.message, type: 'success' },
          bubbles: true, composed: true
        }));
      } else {
        this._result = {
          type: 'error',
          message: data.message || data.error || 'Restore failed.',
          errors: data.errors || null
        };
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.error || 'Some settings failed to restore', type: 'error' },
          bubbles: true, composed: true
        }));
      }
    } catch (e) {
      this._result = { type: 'error', message: e.message };
    }

    this._loading = false;
  }

  render() {
    return html`
      <div class="settings-section setup-card">
        <div class="setup-icon">
          <i class="fa-sharp fa-light fa-wand-magic-sparkles"></i>
        </div>
        <div class="setup-title">Initial Setup</div>
        <p class="setup-desc">
          One-time setup to populate all Voice AI settings with defaults.
          This includes LLM providers, STT/TTS configurations, pipeline settings,
          API keys, and feature toggles. Use this when deploying to a new server
          or to reset all voice settings to their defaults.
        </p>
        <button class="btn-restore" ?disabled=${this._loading}
          @click=${() => { this._showConfirm = true; }}>
          ${this._loading
            ? html`<i class="fa-sharp fa-light fa-spinner fa-spin" style="margin-right:6px;"></i> Restoring...`
            : html`<i class="fa-sharp fa-light fa-rotate-right" style="margin-right:6px;"></i> Restore All Defaults`}
        </button>

        ${this._result ? html`
          <div class="result-box ${this._result.type === 'success' ? 'result-success' : 'result-error'}">
            <i class="fa-sharp fa-light ${this._result.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}"
              style="margin-right:6px;"></i>
            ${this._result.message}
            ${this._result.errors?.length ? html`
              <details style="margin-top:var(--space-2);font-size:var(--text-xs);">
                <summary style="cursor:pointer;font-weight:600;">${this._result.errors.length} failed key(s) — click to expand</summary>
                <ul style="margin-top:var(--space-1);padding-left:var(--space-4);text-align:left;">
                  ${this._result.errors.map(e => html`<li>${e}</li>`)}
                </ul>
              </details>
            ` : ''}
          </div>
        ` : ''}
      </div>

      <voice-modal
        ?open=${this._showConfirm}
        title="Reset Voice AI Settings?"
        width="480px"
        @modal-close=${() => { this._showConfirm = false; }}>
        <div class="modal-warning">
          <p>This will <strong>overwrite ALL Voice AI configuration</strong> with default values, including:</p>
          <ul style="margin:var(--space-2) 0;padding-left:var(--space-4);">
            <li>LLM provider API keys and models</li>
            <li>STT/TTS provider settings</li>
            <li>Pipeline configuration</li>
            <li>Payment and feature toggles</li>
            <li>Google Cloud Vision API key</li>
          </ul>
          <p>Existing voice settings will be <strong>replaced</strong>.
          Non-voice system settings (bookings, payments, display) will <strong>NOT</strong> be affected.</p>
        </div>
        <div slot="footer" class="modal-footer">
          <button class="btn-cancel" @click=${() => { this._showConfirm = false; }}>Cancel</button>
          <button class="btn-confirm-danger" @click=${this._restore}>
            <i class="fa-sharp fa-light fa-triangle-exclamation" style="margin-right:4px;"></i>
            Yes, Restore Defaults
          </button>
        </div>
      </voice-modal>
    `;
  }
}

customElements.define('settings-setup-tab', SettingsSetupTab);
